import { inject } from 'mobx-react';
import * as React from 'react';

import { AnalyticsStore, QueryRoute, RouteStore } from 'bernie-core';
import { Layer } from 'bernie-view';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';

import { CurrentHotelStore } from 'stores/current-hotel-store';

import { GalleryViewModel, ImageViewModel } from 'src/common/models';

import { UitkCard, UitkCardContent, UitkCardMedia, UitkCardTextSegment } from 'uitk-react-cards';
import { UitkFigure } from 'uitk-react-images';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkPill } from 'uitk-react-pill';

import { GalleryDialog } from 'components/infosite/property-details/gallery-dialog';
import { QueuedImage } from 'components/common/media/queued-image';

interface GalleryByCategoriesProps extends LocalizedComponentProps {
  analytics: AnalyticsStore;
  routeStore?: RouteStore;
  currentHotel: CurrentHotelStore;
  gallery: GalleryViewModel;
  trackingRfrr: string;
  id: number;
}

interface GalleryByCategoriesState {
  selectedPill: string;
}

interface ThumbnailComponentProps {
  image: ImageViewModel;
  categoryId: string;
  index: number;
  onImageSelected?: any;
}

// Using an extra component because UitkLayoutGridItem adds the class uitk-layout-grid-item to its first child, which removes li's class.
class ThumbnailComponent extends React.Component<ThumbnailComponentProps> {
  constructor(props) {
    super(props);
  }

  public render(): JSX.Element {
    const { image, categoryId, index, onImageSelected } = this.props;

    return (
      <li className="uitk-media-gallery-thumbnail">
        <UitkFigure ratio={UitkFigure.AspectRatio.R1_1}>
          <QueuedImage key={image.thumbnailSrc} src={image.thumbnailSrc} alt={image.caption} groupId={categoryId} lazyLoad={true} />
        </UitkFigure>
        <button className="uitk-media-gallery-thumbnail-link" onClick={onImageSelected.bind(this, index)}>
          <span className="is-visually-hidden">{`Open large version of image ${image.caption}`}</span>
        </button>
      </li>
    );
  }
}

// tslint:disable-next-line:max-classes-per-file
@withLocalization
@inject('routeStore', 'analytics')
export class GalleryByCategories extends React.Component<GalleryByCategoriesProps, GalleryByCategoriesState> {
  constructor(props) {
    super(props);

    this.state = {
      selectedPill: props.gallery.categories && props.gallery.categories.length > 0 ? props.gallery.categories[0].id : undefined
    };
    this.onImageSelected = this.onImageSelected.bind(this);
    this.openPhotoGalleryDialog = this.openPhotoGalleryDialog.bind(this);
    this.onPillSelect = this.onPillSelect.bind(this);
    this.updateIndex = this.updateIndex.bind(this);
    this.galleryDialogCloseCallback = this.galleryDialogCloseCallback.bind(this);
  }

  public render(): JSX.Element {
    const { gallery, currentHotel, trackingRfrr, id } = this.props;
    const { selectedPill } = this.state;

    const isCategoriesShowable = gallery && gallery.categories && gallery.categories.length > 0 && selectedPill;
    const selectedCategory = isCategoriesShowable ? gallery.categories.filter(x => x.id === selectedPill)[0] : null;
    const selectedImages = selectedCategory ? selectedCategory.images : gallery.images;
    const categoryId = selectedCategory ? selectedCategory.id : 'all';

    const currentGalleryIndex = (currentHotel && currentHotel.photoGalleryIndex) || 0;
    const param = { pwaDialog: `photoGalleryCategory-${id}` };

    return (
      <div>
        <QueryRoute query={param}>
          <Layer id="photoGalleryCategory-trigger" onDismiss={this.galleryDialogCloseCallback}>
            <GalleryDialog
              currentIndex={currentGalleryIndex}
              galleryDialogCloseCallback={this.galleryDialogCloseCallback}
              galleryName={trackingRfrr}
              images={selectedImages}
              onIndexChange={this.updateIndex}
              trackingRfrr={trackingRfrr}
            />
          </Layer>
        </QueryRoute>
        <UitkCard>
          <UitkCardContent>
            {isCategoriesShowable && (
              <UitkCardTextSegment className="uitk-grid all-grid-nowrap uitk-scroll-horizontal">
                {gallery.categories.map(category => (
                  <UitkPill id={category.id} key={category.id} onChange={this.onPillSelect} checked={selectedPill === category.id}>
                    {category.name} ({category.images.length})
                  </UitkPill>
                ))}
              </UitkCardTextSegment>
            )}
            <UitkCardMedia>
              <UitkLayoutGrid
                tag={'ul'}
                columns={{ small: 2, medium: 4, large: 6 }}
                rows={{ small: 1, medium: 2 }}
                space="two"
                className="uitk-media-gallery-thumbnail-view"
              >
                {selectedImages.map((image, idx) => (
                  <UitkLayoutGridItem key={`gallery-thumbnail-${idx}`}>
                    <ThumbnailComponent image={image} categoryId={categoryId} index={idx} onImageSelected={this.onImageSelected} />
                  </UitkLayoutGridItem>
                ))}
              </UitkLayoutGrid>
            </UitkCardMedia>
          </UitkCardContent>
        </UitkCard>
      </div>
    );
  }

  public onPillSelect(evt): void {
    const selectedPill = evt.target.id;
    const { analytics, gallery } = this.props;
    const { categories } = gallery;
    const selectedCategory = categories.find(x => x.id === selectedPill);
    const pillOrder = categories.findIndex(x => x.id === selectedPill);
    analytics.clickTracker(`.PhotoGalleryTrigger.[${selectedCategory.id}][${pillOrder}]`, 'Photo tab pill', null, null)();
    this.setState({ selectedPill });
  }

  private onImageSelected(index: number): void {
    const { gallery, analytics, trackingRfrr } = this.props;
    const { selectedPill } = this.state;
    const { categories } = gallery;
    if (categories && categories.length > 0) {
      analytics.clickTracker(`.${trackingRfrr}.[${selectedPill}][${index}]`, 'Display Photo tab carousel', null, null)();
    } else {
      analytics.clickTracker(`.${trackingRfrr}.[NoCategories][${index}]`, 'Display Photo tab carousel', null, null)();
    }
    this.updateIndex(index);
    this.openPhotoGalleryDialog();
  }

  private updateIndex(index: number): void {
    const { currentHotel } = this.props;

    if (currentHotel && typeof currentHotel.setCurrentGalleryIndex === 'function') {
      currentHotel.setCurrentGalleryIndex(index);
    }
  }

  private openPhotoGalleryDialog(): void {
    this.props.routeStore.updateSearch({ pwaDialog: `photoGalleryCategory-${this.props.id}` });
  }

  private galleryDialogCloseCallback() {
    const { routeStore } = this.props;
    if (routeStore && typeof routeStore.goBack === 'function') {
      routeStore.goBack();
    }
  }
}
